import React from 'react';
import { configureAmplify } from '../config/amplifyConfig';
import { Alert } from '@amzn/awsui-components-core';

interface PropsType {
  children: React.ReactNode;
}

interface StateType {
  error: Error | null;
  hasError: boolean;
}

/**
 * Simple class to manage the application initialization metric.
 *
 * Note this is meant as a demonstration of how to use the initialization metric, and
 * is almost certainly inadequate for a more complex application.
 *
 * It waits for itself to be mounted (if it is the outermost component that means all of
 * its children are rendered and mounted by React, though are not necessarily fully rendered
 * in the browser), then waits for the translations to be ready.
 *
 * In your actual application, you are likely not fully initialized until you have completed
 * your initial REST calls.  You will need to either integrate that logic here, or else move
 * the initialization metric elsewhere.
 *
 * For applications using a state manager (e.g. Redux) it may make more sense to determine if
 * your application is initialized from that layer instead.
 */
export class AppInitWrapper extends React.Component<PropsType, StateType> {
  constructor(props: PropsType) {
    super(props);
    this.state = {
      error: null,
      hasError: false,
    };
  }

  componentDidMount(): void {
    try {
      // Configure only Amplify with Cognito during initial app load
      // AppSync will be configured after authentication in AuthContext
      configureAmplify();
    } catch (error) {
      console.error('Failed to configure Amplify:', error);
      this.setState({
        error: error instanceof Error ? error : new Error('Failed to configure authentication'),
        hasError: true,
      });
    }
  }

  componentDidCatch(error: Error, _info: React.ErrorInfo): void {
    // Update state to display error message
    this.setState({ error, hasError: true });
    console.error('AppInitWrapper caught an error:', error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px' }}>
          <Alert type="error" header="Application initialization failed" dismissible={false}>
            {this.state.error?.message || 'An unexpected error occurred during application initialization.'}
            <p>Please try refreshing the page. If the problem persists, contact support.</p>
          </Alert>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppInitWrapper;
